import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';

import { NumberCell } from '@feathr/components';

export interface ITrafficBreakdownRow {
  id: string;
  source: string;
  views: number;
  sessions: number;
  conversions: number;
}

// Derived from the row shape so the two can't drift; totals cover every numeric column.
export type ITrafficBreakdownTotals = Omit<ITrafficBreakdownRow, 'id' | 'source'>;

// Known utm_source values are shown as friendly labels; unmapped sources render raw.
const sourceLabelMap: Record<string, string> = {
  email: 'Email Marketing',
  search: 'Paid Search',
  facebook: 'Meta',
  twitter: 'Twitter',
  linkedin: 'LinkedIn',
};

export function getTrafficBreakdownColumns(
  t: TFunction,
  totals: ITrafficBreakdownTotals,
): Array<ColumnDef<ITrafficBreakdownRow>> {
  return [
    {
      id: 'source',
      accessorFn: (row): string => row.source,
      header: t('Source'),
      size: 450,
      sortDescFirst: false,
      cell: ({ row }: CellContext<ITrafficBreakdownRow, unknown>): JSX.Element => {
        const { source } = row.original;
        return <>{sourceLabelMap[source] || source}</>;
      },
      footer: (): string => t('Total'),
    },
    {
      id: 'views',
      accessorFn: (row): number => row.views,
      header: t('Views'),
      meta: { align: 'right' },
      // Ascending-first, matching the Source column and the sibling table migrations.
      sortDescFirst: false,
      cell: ({ row }: CellContext<ITrafficBreakdownRow, unknown>): JSX.Element => (
        <NumberCell format={'count'} value={row.original.views} />
      ),
      footer: (): JSX.Element => <NumberCell format={'count'} value={totals.views} />,
    },
    {
      id: 'sessions',
      accessorFn: (row): number => row.sessions,
      header: t('Sessions'),
      meta: { align: 'right' },
      sortDescFirst: false,
      cell: ({ row }: CellContext<ITrafficBreakdownRow, unknown>): JSX.Element => (
        <NumberCell format={'count'} value={row.original.sessions} />
      ),
      footer: (): JSX.Element => <NumberCell format={'count'} value={totals.sessions} />,
    },
    {
      id: 'conversions',
      accessorFn: (row): number => row.conversions,
      header: t('Conversions'),
      meta: { align: 'right' },
      size: 140,
      sortDescFirst: false,
      cell: ({ row }: CellContext<ITrafficBreakdownRow, unknown>): JSX.Element => (
        <NumberCell format={'count'} value={row.original.conversions} />
      ),
      footer: (): JSX.Element => <NumberCell format={'count'} value={totals.conversions} />,
    },
  ];
}
